import {
  PDFDocument,
  rgb,
  pushGraphicsState,
  popGraphicsState,
  rectangle,
  clip,
  endPath,
  degrees,
} from "pdf-lib";
import { mmToPt } from "./units";
import { slotsForPage, computeLayout } from "./imposition";

// Re-encode an image via canvas. This produces a clean baseline JPEG/PNG that
// pdf-lib can always embed (avoids the classic "SOI not found" error caused by
// progressive JPEGs, unusual color profiles or non-baseline encodings).
async function reencodeViaCanvas(dataUrl, asPng = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        if (!asPng) {
          // Fill white background to flatten transparency for JPEG.
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0);
        const out = asPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.95);
        resolve(out);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("No se pudo decodificar la imagen"));
    img.src = dataUrl;
  });
}

function dataUrlToBytes(dataUrl) {
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Image is identified by its data URL (PNG or JPEG). Falls back to canvas
// re-encoding when pdf-lib refuses to embed (e.g., progressive JPEG).
async function embedImage(pdfDoc, dataUrl) {
  const mimeMatch = dataUrl.match(/^data:([^;]+);base64,/);
  const mime = mimeMatch ? mimeMatch[1].toLowerCase() : "";
  const isPng = mime === "image/png";
  const isJpg = mime === "image/jpeg" || mime === "image/jpg";

  // First attempt: embed the original bytes.
  try {
    const bytes = dataUrlToBytes(dataUrl);
    if (isPng) return await pdfDoc.embedPng(bytes);
    if (isJpg) return await pdfDoc.embedJpg(bytes);
  } catch (_) {
    // Fall through to canvas re-encode.
  }

  // Fallback: re-encode the image via canvas (baseline JPEG or PNG).
  const reencoded = await reencodeViaCanvas(dataUrl, isPng);
  const bytes = dataUrlToBytes(reencoded);
  if (isPng) return await pdfDoc.embedPng(bytes);
  return await pdfDoc.embedJpg(bytes);
}

// Compute the image draw rect inside a frame in PDF coordinates (origin bottom-left).
// frame is in PDF pt; image natural is px; slot.adjust has fit/zoom/offset/rotation/scaleX/scaleY/distort.
function computeImageDrawRect(framePtX, framePtY, framePtW, framePtH, imgW, imgH, adjust) {
  const fit = adjust.fit || "fill";
  const zoom = Math.max(0.05, adjust.zoom || 1);
  const offsetXmm = adjust.offset_x || 0;
  const offsetYmm = adjust.offset_y || 0;

  // Scale to fit or fill the frame
  const sFit = Math.min(framePtW / imgW, framePtH / imgH);
  const sFill = Math.max(framePtW / imgW, framePtH / imgH);
  const sBase = fit === "fit" ? sFit : sFill;
  const sX = adjust.distort ? sBase * (adjust.scaleX || 1) : sBase * zoom;
  const sY = adjust.distort ? sBase * (adjust.scaleY || 1) : sBase * zoom;

  const drawW = imgW * sX;
  const drawH = imgH * sY;
  // Center inside frame, then shift by offsets (note: PDF y goes UP, but offset_y means "down" in UI -> negate)
  const cx = framePtX + framePtW / 2 + mmToPt(offsetXmm);
  const cy = framePtY + framePtH / 2 - mmToPt(offsetYmm);
  return {
    x: cx - drawW / 2,
    y: cy - drawH / 2,
    w: drawW,
    h: drawH,
  };
}

// Draw four corner cut marks for a rectangle (in PDF pt).
function drawCutMarks(page, x, y, w, h, opts) {
  const lenPt = mmToPt(opts.length_mm);
  const distPt = mmToPt(opts.distance_mm);
  const thickness = opts.weight_pt;
  const color = rgb(0, 0, 0);

  const corners = [
    { cx: x, cy: y, sx: -1, sy: -1 }, // bottom-left  (PDF coords)
    { cx: x + w, cy: y, sx: 1, sy: -1 }, // bottom-right
    { cx: x, cy: y + h, sx: -1, sy: 1 }, // top-left
    { cx: x + w, cy: y + h, sx: 1, sy: 1 }, // top-right
  ];

  for (const c of corners) {
    // Horizontal tick
    page.drawLine({
      start: { x: c.cx + c.sx * distPt, y: c.cy },
      end: { x: c.cx + c.sx * (distPt + lenPt), y: c.cy },
      thickness,
      color,
    });
    // Vertical tick
    page.drawLine({
      start: { x: c.cx, y: c.cy + c.sy * distPt },
      end: { x: c.cx, y: c.cy + c.sy * (distPt + lenPt) },
      thickness,
      color,
    });
  }
}

function drawBorderFrame(page, x, y, w, h, opts) {
  page.drawRectangle({
    x,
    y,
    width: w,
    height: h,
    borderColor: rgb(0, 0, 0),
    borderWidth: opts.weight_pt,
  });
}

export async function exportProjectToPDF(state) {
  const { page, piece, copies, separation, bleed, cutMarks, images, slotAdjustments, pageOffsets } = state;

  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle("KAPEI Imposer PRO");
  pdfDoc.setCreator("KAPEI Imposer PRO");
  pdfDoc.setProducer("KAPEI Imposer PRO");

  // Embed all unique images once.
  const embedded = {};
  for (const img of images) {
    embedded[img.id] = await embedImage(pdfDoc, img.dataUrl);
  }

  const layout = computeLayout({ page, piece, copies, separation, bleed });
  if (layout.pages === 0) {
    throw new Error("No caben piezas en la hoja con la configuración actual.");
  }

  const pagePtW = mmToPt(page.width_mm);
  const pagePtH = mmToPt(page.height_mm);

  for (let pageIndex = 0; pageIndex < layout.pages; pageIndex++) {
    const pdfPage = pdfDoc.addPage([pagePtW, pagePtH]);
    const { slots } = slotsForPage({
      page,
      piece,
      copies,
      separation,
      bleed,
      pageIndex,
    });

    const pageOffset = (pageOffsets && pageOffsets[pageIndex]) || { x: 0, y: 0 };
    const offPtX = mmToPt(pageOffset.x || 0);
    const offPtY = mmToPt(pageOffset.y || 0);

    for (const slot of slots) {
      const adj = slotAdjustments[slot.index] || {
        imageId: null,
        fit: "fill",
        zoom: 1,
        offset_x: 0,
        offset_y: 0,
        rotation: 0,
        distort: false,
        scaleX: 1,
        scaleY: 1,
      };

      // Frame rectangle in PDF coords (origin bottom-left).
      // UI uses top-left origin (y down). Convert: pdf_y = pageH - ui_y - height
      // pageOffset shifts the whole block (Y in UI = down, so PDF y subtracts).
      const bleedX = mmToPt(slot.bleed_x_mm) + offPtX;
      const bleedY = pagePtH - mmToPt(slot.bleed_y_mm) - mmToPt(slot.bleed_h_mm) - offPtY;
      const bleedW = mmToPt(slot.bleed_w_mm);
      const bleedH = mmToPt(slot.bleed_h_mm);

      const cutX = mmToPt(slot.x_mm) + offPtX;
      const cutY = pagePtH - mmToPt(slot.y_mm) - mmToPt(slot.height_mm) - offPtY;
      const cutW = mmToPt(slot.width_mm);
      const cutH = mmToPt(slot.height_mm);

      // Draw the image clipped to the BLEED rectangle (so bleed area is filled with image too)
      const img = adj.imageId ? embedded[adj.imageId] : null;
      if (img) {
        const rect = computeImageDrawRect(
          bleedX,
          bleedY,
          bleedW,
          bleedH,
          img.width,
          img.height,
          adj
        );
        // Clip via push/pop graphics state with a clipping path so the image
        // never overflows into neighbour pieces.
        pdfPage.pushOperators(
          pushGraphicsState(),
          rectangle(bleedX, bleedY, bleedW, bleedH),
          clip(),
          endPath()
        );
        pdfPage.drawImage(img, {
          x: rect.x,
          y: rect.y,
          width: rect.w,
          height: rect.h,
          rotate: adj.rotation ? degrees(-adj.rotation) : undefined,
        });
        pdfPage.pushOperators(popGraphicsState());
      }

      // Optional border frame around the CUT rect (thin black frame)
      if (cutMarks.showBorderFrame) {
        drawBorderFrame(pdfPage, cutX, cutY, cutW, cutH, cutMarks);
      }
    }

    // Cut marks: drawn relative to the CUT rectangle (not the bleed). For each unique slot
    // OR only for the outer block depending on cutMarks.kind.
    if (cutMarks.enabled && shouldDrawMarksOnPage(pageIndex, layout.pages, cutMarks)) {
      if (cutMarks.kind === "block_outer") {
        const minXmm = Math.min(...slots.map((s) => s.x_mm));
        const minYmm = Math.min(...slots.map((s) => s.y_mm));
        const maxXmm = Math.max(...slots.map((s) => s.x_mm + s.width_mm));
        const maxYmm = Math.max(...slots.map((s) => s.y_mm + s.height_mm));
        const ox = mmToPt(minXmm) + offPtX;
        const oy = pagePtH - mmToPt(maxYmm) - offPtY;
        const ow = mmToPt(maxXmm - minXmm);
        const oh = mmToPt(maxYmm - minYmm);
        drawCutMarks(pdfPage, ox, oy, ow, oh, cutMarks);
      } else {
        for (const slot of slots) {
          const cutX = mmToPt(slot.x_mm) + offPtX;
          const cutY = pagePtH - mmToPt(slot.y_mm) - mmToPt(slot.height_mm) - offPtY;
          const cutW = mmToPt(slot.width_mm);
          const cutH = mmToPt(slot.height_mm);
          drawCutMarks(pdfPage, cutX, cutY, cutW, cutH, cutMarks);
        }
      }
    }
  }

  const bytes = await pdfDoc.save();
  return bytes;
}

function shouldDrawMarksOnPage(pageIndex, totalPages, cutMarks) {
  const mode = cutMarks.pages || "all";
  if (mode === "all") return true;
  if (mode === "first") return pageIndex === 0;
  if (mode === "odd") return (pageIndex + 1) % 2 === 1;
  if (mode === "even") return (pageIndex + 1) % 2 === 0;
  return true;
}

export function buildPdfFilename() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  return `KAPEI_Imposer_PRO_${ts}.pdf`;
}

export function downloadPdfBytes(bytes, filename) {
  const name = filename || buildPdfFilename();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

// Robust print: downloads the PDF (so user has a copy in /Downloads) AND opens
// a new tab so the browser can show the OS print dialog. Both paths are safe
// — if popups are blocked the download still works.
export function openPdfForPrint(bytes) {
  const name = buildPdfFilename();
  downloadPdfBytes(bytes, name);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (w) {
    w.addEventListener("load", () => {
      try {
        w.focus();
        w.print();
      } catch (e) {
        // ignore
      }
    });
  }
  return { filename: name, popupBlocked: !w };
}
